<script>
  import { fetchUnitValidation, getErrorMessage } from '$lib/api';
  import { onMount, onDestroy, tick } from 'svelte';
  import { get } from 'svelte/store';
  import { selectEntity, clearEntity } from '$lib/router';
  import { workspaceMode, setWorkspaceMode } from '../../workspaceMode';
  import { router } from '$lib/router';
  import { uiState } from '../../state/ui.svelte';
  import { showError } from '../../state/toast.svelte';
  import { downloadMappings } from '../../export/firmware';
  import { unitsQuery } from '../../queries/units';
  import { zonesQuery } from '../../queries/zones';
  import { roomsQuery } from '../../queries/rooms';
  import { loadsQuery } from '../../queries/loads';
  import { inputDevicesQuery } from '../../queries/input_devices';
  import { rcusQuery } from '../../queries/rcus';
  import { dinModulesForUnitQuery } from '../../queries/din_modules';
  import { scenesQuery } from '../../queries/scenes';
  import { computeUnitWorkflow } from '../../workflow';
  import { downloadLogicSheet } from '../../unitExports';
  import {
    createZone,
    createRoom,
    createLoad,
    createDevice,
    createScene,
    promptZoneForRoom,
    promptRoomForChild
  } from '../../unitCreate';

  const units = unitsQuery();
  const zones = zonesQuery();
  const rooms = roomsQuery();
  const loads = loadsQuery();
  const keypads = inputDevicesQuery();
  const rcus = rcusQuery();
  const dinModules = dinModulesForUnitQuery();
  const scenes = scenesQuery();

  let open = false;
  let query = '';
  let selectedIndex = 0;
  /** @type {HTMLInputElement | null} */
  let inputEl = null;

  /** @type {'main' | 'pick-zone' | 'pick-room'} */
  let step = 'main';
  /** @type {'room' | 'load' | 'keypad' | null} */
  let pendingCreate = null;

  let unit = $derived((units.data ?? []).find((u) => u.id === router.unitId) ?? null);
  let unitZones = $derived(zones.data ?? []);
  let unitRooms = $derived(rooms.data ?? []);
  let unitLoads = $derived(loads.data ?? []);
  let unitDevices = $derived(keypads.data ?? []);

  let unitRcus = $derived(unit ? (rcus.data ?? []).filter((rcu) => rcu.unit === unit.id) : []);
  let unitDinModules = $derived(dinModules.data ?? []);
  let unitScenes = $derived(
    unit ? (scenes.data ?? []).filter((scene) => scene.unit === unit.id) : []
  );

  let workflow = $derived(
    computeUnitWorkflow({
      unit: unit,
      zones: unitZones,
      rooms: unitRooms,
      loads: unitLoads,
      keypads: unitDevices,
      keypadKeys: [],
      keyActions: [],
      rcus: rcus.data ?? [],
      dinModules: dinModules.data ?? [],
      channels: [],
      scenes: scenes.data ?? [],
      sceneLoads: [],
      products: []
    })
  );

  /** @type {Array<{ id: string, section: string, label: string, type: string, run: () => void | Promise<void>, searchText?: string }>} */
  let jumpItems = $derived(
    buildJumpItems(
      unit,
      unitZones,
      unitRooms,
      unitLoads,
      unitRcus,
      unitDinModules,
      unitDevices,
      unitScenes
    )
  );

  /** @type {Array<{ id: string, section: string, label: string, type: string, run: () => void | Promise<void> }>} */
  let createItems = $derived([
    {
      id: 'new-zone',
      section: 'Create',
      label: 'New Zone',
      type: 'action',
      run: () => runCreate(() => createZone())
    },
    {
      id: 'new-room',
      section: 'Create',
      label: 'New Room…',
      type: 'action',
      run: () => startCreate('room')
    },
    {
      id: 'new-load',
      section: 'Create',
      label: 'New Load…',
      type: 'action',
      run: () => startCreate('load')
    },
    {
      id: 'new-keypad',
      section: 'Create',
      label: 'New Keypad…',
      type: 'action',
      run: () => startCreate('keypad')
    },
    {
      id: 'new-scene',
      section: 'Create',
      label: 'New Scene…',
      type: 'action',
      run: () => runCreate(() => createScene())
    }
  ]);

  /** @type {Array<{ id: string, section: string, label: string, type: string, run: () => void | Promise<void> }>} */
  let actionItems = $derived([
    {
      id: 'validate',
      section: 'Actions',
      label: 'Run validation',
      type: 'action',
      run: runValidation
    },
    {
      id: 'export-v3',
      section: 'Actions',
      label: 'Export firmware (V3)',
      type: 'action',
      run: () =>
        runCreate(async () => {
          await downloadMappings('v3');
        })
    },
    {
      id: 'export-logic',
      section: 'Actions',
      label: 'Export logic sheet',
      type: 'action',
      run: () =>
        runCreate(async () => {
          await downloadLogicSheet();
        })
    },
    {
      id: 'toggle-mode',
      section: 'Actions',
      label: 'Toggle commissioning mode',
      type: 'action',
      run: toggleCommissioning
    },
    {
      id: 'browse-loads',
      section: 'Actions',
      label: 'Browse all loads',
      type: 'view',
      run: () => {
        selectEntity('browse-loads', null);
        close();
      }
    },
    {
      id: 'led-logic',
      section: 'Actions',
      label: 'LED Logic',
      type: 'view',
      run: () => {
        const first = unitDevices[0];
        if (first) selectEntity('led-logic', first.id);
        close();
      }
    },
    {
      id: 'products',
      section: 'Actions',
      label: 'Products & BOM',
      type: 'view',
      run: () => {
        selectEntity('products', null);
        close();
      }
    }
  ]);

  let allItems = $derived([...jumpItems, ...createItems, ...actionItems]);

  let filteredItems = $derived(filterItems(allItems, query));

  $effect(() => {
    if (selectedIndex >= filteredItems.length) {
      selectedIndex = Math.max(0, filteredItems.length - 1);
    }
  });

  function buildJumpItems(unit, zones, rooms, loads, rcusList, dins, keypads, sceneList) {
    if (!unit) return [];
    /** @type {typeof allItems} */
    const items = [];

    items.push({
      id: 'unit',
      section: 'Jump',
      label: unit.name,
      type: 'unit',
      searchText: unit.name,
      run: () => {
        clearEntity();
        close();
      }
    });

    for (const zone of zones) {
      items.push({
        id: `zone-${zone.id}`,
        section: 'Jump',
        label: zone.name,
        type: 'zone',
        searchText: zone.name,
        run: () => {
          selectEntity('zone', zone.id);
          close();
        }
      });
    }

    for (const room of rooms) {
      items.push({
        id: `room-${room.id}`,
        section: 'Jump',
        label: room.name,
        type: 'room',
        searchText: room.name,
        run: () => {
          selectEntity('room', room.id);
          close();
        }
      });
    }

    for (const load of loads) {
      items.push({
        id: `load-${load.id}`,
        section: 'Jump',
        label: load.name,
        type: 'load',
        searchText: load.name,
        run: () => {
          selectEntity('load', load.id);
          close();
        }
      });
    }

    for (const rcu of rcusList) {
      items.push({
        id: `rcu-${rcu.id}`,
        section: 'Jump',
        label: rcu.name || `RCU ${rcu.id}`,
        type: 'rcu',
        searchText: rcu.name || `RCU ${rcu.id}`,
        run: () => {
          selectEntity('rcu', rcu.id);
          close();
        }
      });
    }

    for (const din of dins) {
      items.push({
        id: `din-${din.id}`,
        section: 'Jump',
        label: `DIN ${din.address ?? din.id}`,
        type: 'din',
        searchText: `DIN ${din.address ?? din.id}`,
        run: () => {
          selectEntity('din', din.id);
          close();
        }
      });
    }

    for (const keypad of keypads) {
      const room = rooms.find((r) => r.id === keypad.location_room);
      const label = room ? `Keypad in ${room.name}` : `Keypad ${keypad.address}`;
      items.push({
        id: `keypad-${keypad.id}`,
        section: 'Jump',
        label,
        type: 'keypad',
        searchText: label,
        run: () => {
          selectEntity('keypad', keypad.id);
          close();
        }
      });
      items.push({
        id: `led-logic-${keypad.id}`,
        section: 'Jump',
        label: `LED: ${label}`,
        type: 'led-logic',
        searchText: `LED ${label}`,
        run: () => {
          selectEntity('led-logic', keypad.id);
          close();
        }
      });
    }

    for (const scene of sceneList) {
      items.push({
        id: `scene-${scene.id}`,
        section: 'Jump',
        label: scene.name,
        type: 'scene',
        searchText: scene.name,
        run: () => {
          selectEntity('scene', scene.id);
          close();
        }
      });
    }

    return items;
  }

  function fuzzyMatch(queryText, target) {
    const q = queryText.toLowerCase().trim();
    if (!q) return true;
    const t = target.toLowerCase();
    let ti = 0;
    for (const char of q) {
      ti = t.indexOf(char, ti);
      if (ti === -1) return false;
      ti += 1;
    }
    return true;
  }

  function filterItems(items, q) {
    if (!q.trim()) return items;
    return items.filter((item) => fuzzyMatch(q, item.searchText ?? item.label));
  }

  function openPalette() {
    if (!unit) return;
    open = true;
    query = '';
    step = 'main';
    pendingCreate = null;
    selectedIndex = 0;
    tick().then(() => inputEl?.focus());
  }

  function close() {
    open = false;
    query = '';
    step = 'main';
    pendingCreate = null;
  }

  async function runCreate(fn) {
    close();
    await fn();
  }

  /** @param {'room' | 'load' | 'keypad'} kind */
  async function startCreate(kind) {
    close();
    if (kind === 'room') {
      const zoneId = await promptZoneForRoom();
      if (zoneId) await createRoom(zoneId);
      return;
    }
    const roomId = await promptRoomForChild(kind === 'load' ? 'New load' : 'New device');
    if (!roomId) return;
    if (kind === 'load') await createLoad(roomId);
    else await createDevice(roomId);
  }

  async function runValidation() {
    close();
    if (!unit) return;
    try {
      const result = await fetchUnitValidation(unit.id);
      uiState.validationResults = result?.errors || [];
      const first = workflow.issues[0];
      if (first?.entity_id != null) {
        selectEntity(first.entity_type, first.entity_id);
      }
      document.dispatchEvent(new CustomEvent('expand-rail-issues'));
    } catch (error) {
      showError(getErrorMessage(error));
    }
  }

  function toggleCommissioning() {
    setWorkspaceMode(get(workspaceMode) === 'commissioning' ? 'design' : 'commissioning');
    close();
  }

  function activateItem(item) {
    if (!item) return;
    item.run();
  }

  function handleKeydown(event) {
    if (!open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      selectedIndex = Math.min(selectedIndex + 1, filteredItems.length - 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      selectedIndex = Math.max(selectedIndex - 1, 0);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      activateItem(filteredItems[selectedIndex]);
    }
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) close();
  }

  function onOpenEvent() {
    openPalette();
  }

  onMount(() => {
    document.addEventListener('open-command-palette', onOpenEvent);
    document.addEventListener('keydown', handleKeydown);
  });

  onDestroy(() => {
    document.removeEventListener('open-command-palette', onOpenEvent);
    document.removeEventListener('keydown', handleKeydown);
  });

  let sections = $derived([...new Set(filteredItems.map((item) => item.section))]);
</script>

{#if open}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div
    class="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] bg-black/30 backdrop-in"
    on:click={handleBackdropClick}
    role="presentation"
  >
    <div
      class="w-full max-w-[560px] bg-bg-canvas border border-border-strong rounded shadow-toyama-md overflow-hidden pop-in"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div class="border-b border-border-default px-3 py-2">
        <input
          bind:this={inputEl}
          bind:value={query}
          type="text"
          class="w-full bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none"
          placeholder="Search or run a command…"
          on:keydown={handleKeydown}
        />
      </div>

      <div class="max-h-[320px] overflow-y-auto py-1">
        {#if filteredItems.length === 0}
          <p class="px-3 py-4 text-sm text-text-muted text-center">No matches</p>
        {:else}
          {#each sections as section}
            <p class="px-3 pt-2 pb-1 text-2xs uppercase tracking-wide text-text-muted">
              {section}
            </p>
            {#each filteredItems.filter((item) => item.section === section) as item, i}
              {@const globalIndex = filteredItems.indexOf(item)}
              <button
                type="button"
                class="flex w-full items-center justify-between px-3 py-1.5 text-sm text-left transition-colors duration-100 {globalIndex ===
                selectedIndex
                  ? 'bg-bg-selected'
                  : 'hover:bg-bg-hover'}"
                on:mouseenter={() => (selectedIndex = globalIndex)}
                on:click={() => activateItem(item)}
              >
                <span class="text-text-primary truncate">{item.label}</span>
                <span class="ml-2 shrink-0 text-2xs text-text-muted capitalize">
                  {item.type}
                </span>
              </button>
            {/each}
          {/each}
        {/if}
      </div>
    </div>
  </div>
{/if}
